import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import z from "zod";

export const getNameSchema = (t: TFunction) =>
    z
        .string()
        .trim()
        .min(1, { error: t("TagManagementTable_Error_CannotBeBlank") })
        .max(255, { error: t("TagManagementTable_Error_TooLong") });

function isUniqueName(value: string, existingNames: string[]): boolean {
    if (existingNames.length === 0) {
        return true; // Skip uniqueness check if no existing names
    }
    return existingNames.every((name) => !Cmp.citextEq(name, value));
}
export const refinedNewNameSchema = (t: TFunction, existingNames: string[]) =>
    getNameSchema(t).refine((value) => isUniqueName(value, existingNames), {
        error: t("TagManagementTable_Error_AlreadyExists"),
    });

export const refinedCreateTagNameSchema = (
    t: TFunction,
    existingTagNames: string[]
) =>
    z
        .string()
        .trim()
        .max(255, { error: t("TagManagementTable_Error_TooLong") })
        .refine((value) => isUniqueName(value, existingTagNames), {
            error: t("TagManagementTable_Error_AlreadyExists"),
        });

export function useControlProgramTagCategoryFormSchema(
    existingTagNames: string[]
) {
    const { t } = useTranslation();

    return z.object({
        newTagName: refinedNewNameSchema(t, existingTagNames),
        tags: z.array(
            z.object({
                tagId: z.string(),
                tagName: getNameSchema(t),
            })
        ),
    });
}

export function useControlProgramTagCategoryCreateFormSchema(
    existingTagNames: string[],
    existingCategoryNames: string[]
) {
    const { t } = useTranslation();

    return z.object({
        categoryName: refinedNewNameSchema(t, existingCategoryNames),
        newTagName: refinedCreateTagNameSchema(t, existingTagNames),
        tags: z.array(
            z.object({
                id: z.string(),
                name: getNameSchema(t),
            })
        ),
    });
}
export type ControlProgramTagCategoryFormSchema = z.infer<
    ReturnType<typeof useControlProgramTagCategoryFormSchema>
>;

export type ControlProgramTagCategoryCreateFormSchema = z.infer<
    ReturnType<typeof useControlProgramTagCategoryCreateFormSchema>
>;
